"use client"

import * as React from 'react'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@open-mercato/ui/primitives/dialog'
import { Button } from '@open-mercato/ui/primitives/button'
import { Input } from '@open-mercato/ui/primitives/input'
import { DateTimePicker } from '@open-mercato/ui/backend/inputs/DateTimePicker'
import { Label } from '@open-mercato/ui/primitives/label'
import type { LegListItem } from './legTable'

type SuggestionVehicle = {
  id: string
  registrationPlate: string
  type: string
}

type SuggestionDriver = {
  id: string
  firstName: string
  lastName: string
}

type SuggestionsResult = {
  vehicles: SuggestionVehicle[]
  drivers: SuggestionDriver[]
  window: { start: string | null; end: string | null }
}

type AssignmentDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  leg: LegListItem | null
  onSuccess: () => void
}

export function AssignmentDialog({ open, onOpenChange, leg, onSuccess }: AssignmentDialogProps) {
  const t = useT()
  const { runMutation } = useGuardedMutation({
    contextId: leg ? `dispatch-assign-${leg.id}` : 'dispatch-assign',
  })

  const [vehicleId, setVehicleId] = React.useState('')
  const [driverId, setDriverId] = React.useState('')
  const [trailerId, setTrailerId] = React.useState('')
  const [plannedPickupAt, setPlannedPickupAt] = React.useState<Date | null>(null)
  const [plannedDeliveryAt, setPlannedDeliveryAt] = React.useState<Date | null>(null)
  const [dispatcherNotes, setDispatcherNotes] = React.useState('')
  const [pending, setPending] = React.useState(false)
  const [suggestions, setSuggestions] = React.useState<SuggestionsResult | null>(null)
  const [suggestionsLoading, setSuggestionsLoading] = React.useState(false)

  React.useEffect(() => {
    if (!open) {
      setVehicleId('')
      setDriverId('')
      setTrailerId('')
      setPlannedPickupAt(null)
      setPlannedDeliveryAt(null)
      setDispatcherNotes('')
      setSuggestions(null)
    }
  }, [open])

  const handleGetSuggestions = React.useCallback(async () => {
    if (!leg) return
    setSuggestionsLoading(true)
    try {
      const body: Record<string, unknown> = { legId: leg.id }
      if (plannedPickupAt) body.plannedPickupAt = plannedPickupAt.toISOString()
      if (plannedDeliveryAt) body.plannedDeliveryAt = plannedDeliveryAt.toISOString()
      const result = await readApiResultOrThrow<SuggestionsResult>(
        '/api/road/dispatch/suggestions',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
        { errorMessage: t('dispatch.assign.messages.suggestionsError', 'Failed to load suggestions.') },
      )
      setSuggestions(result)
    } catch {
      flash(t('dispatch.assign.messages.suggestionsError', 'Failed to load suggestions.'), 'error')
    } finally {
      setSuggestionsLoading(false)
    }
  }, [leg, plannedPickupAt, plannedDeliveryAt, t])

  const handleSubmit = React.useCallback(async () => {
    if (!leg || !vehicleId || !driverId) return
    setPending(true)
    try {
      const payload: Record<string, unknown> = {
        legId: leg.id,
        vehicleId,
        driverId,
      }
      if (trailerId) payload.trailerId = trailerId
      if (plannedPickupAt) payload.plannedPickupAt = plannedPickupAt.toISOString()
      if (plannedDeliveryAt) payload.plannedDeliveryAt = plannedDeliveryAt.toISOString()
      if (dispatcherNotes) payload.dispatcherNotes = dispatcherNotes

      await runMutation({
        context: { kind: 'road.dispatch', record: { legId: leg.id } },
        mutationPayload: payload,
        operation: async () => {
          await createCrud('road/dispatch/assignments', payload, {
            errorMessage: t('dispatch.assign.messages.assignError', 'Failed to assign dispatch.'),
          })
        },
      })
      flash(t('dispatch.assign.messages.assignSuccess', 'Dispatch assigned.'), 'success')
      onOpenChange(false)
      onSuccess()
    } finally {
      setPending(false)
    }
  }, [leg, vehicleId, driverId, trailerId, plannedPickupAt, plannedDeliveryAt, dispatcherNotes, runMutation, t, onOpenChange, onSuccess])

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      void handleSubmit()
    }
    if (e.key === 'Escape') {
      onOpenChange(false)
    }
  }, [handleSubmit, onOpenChange])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onKeyDown={handleKeyDown} className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('dispatch.assign.dialog.title', 'Assign vehicle and driver')}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="vehicleId">{t('dispatch.assign.fields.vehicleId', 'Vehicle')}</Label>
            <Input
              id="vehicleId"
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              placeholder="Vehicle ID"
              disabled={pending}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="driverId">{t('dispatch.assign.fields.driverId', 'Driver')}</Label>
            <Input
              id="driverId"
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              placeholder="Driver ID"
              disabled={pending}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="trailerId">{t('dispatch.assign.fields.trailerId', 'Trailer (optional)')}</Label>
            <Input
              id="trailerId"
              value={trailerId}
              onChange={(e) => setTrailerId(e.target.value)}
              placeholder="Trailer ID"
              disabled={pending}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="plannedPickupAt">{t('dispatch.assign.fields.plannedPickupAt', 'Planned pickup')}</Label>
              <DateTimePicker
                value={plannedPickupAt}
                onChange={setPlannedPickupAt}
                disabled={pending}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="plannedDeliveryAt">{t('dispatch.assign.fields.plannedDeliveryAt', 'Planned delivery')}</Label>
              <DateTimePicker
                value={plannedDeliveryAt}
                onChange={setPlannedDeliveryAt}
                disabled={pending}
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="dispatcherNotes">{t('dispatch.assign.fields.dispatcherNotes', 'Dispatcher notes')}</Label>
            <Input
              id="dispatcherNotes"
              value={dispatcherNotes}
              onChange={(e) => setDispatcherNotes(e.target.value)}
              placeholder=""
              disabled={pending}
            />
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => { void handleGetSuggestions() }}
            disabled={suggestionsLoading || pending || !leg}
          >
            {suggestionsLoading
              ? '…'
              : t('dispatch.actions.getSuggestions', 'Get suggestions')}
          </Button>

          {suggestions && (
            <div className="rounded-md border p-3 text-sm space-y-2">
              <p className="font-medium">{t('dispatch.assign.suggestions.title', 'Suggestions')}</p>
              {suggestions.vehicles.length === 0 && suggestions.drivers.length === 0 ? (
                <p className="text-muted-foreground">
                  {t('dispatch.assign.suggestions.noSuggestions', 'No available vehicles or drivers found for this time window.')}
                </p>
              ) : (
                <>
                  {suggestions.vehicles.length > 0 && (
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">
                        {t('dispatch.assign.suggestions.availableVehicles', 'Available vehicles')}
                      </p>
                      <ul className="space-y-1">
                        {suggestions.vehicles.map((v) => (
                          <li key={v.id}>
                            <button
                              type="button"
                              className="text-left hover:underline"
                              onClick={() => setVehicleId(v.id)}
                            >
                              {v.registrationPlate} ({v.type})
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {suggestions.drivers.length > 0 && (
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">
                        {t('dispatch.assign.suggestions.availableDrivers', 'Available drivers')}
                      </p>
                      <ul className="space-y-1">
                        {suggestions.drivers.map((d) => (
                          <li key={d.id}>
                            <button
                              type="button"
                              className="text-left hover:underline"
                              onClick={() => setDriverId(d.id)}
                            >
                              {d.lastName} {d.firstName}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            {t('dispatch.actions.cancelEdit', 'Cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => { void handleSubmit() }}
            disabled={pending || !vehicleId || !driverId}
          >
            {pending ? t('dispatch.actions.saving', 'Saving…') : t('dispatch.assign.dialog.submit', 'Assign')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
